import React, { useState, useEffect } from 'react';
import { Sun, ChevronDown, RefreshCw, ExternalLink, Clock, AlertTriangle, FileText, DollarSign, Sparkles, CheckCircle } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import api from '../services/api';

const URGENCY_STYLES = {
  now: { bg: 'bg-red-900/40', border: 'border-red-700', text: 'text-red-300', badge: 'bg-red-600 text-white' },
  today: { bg: 'bg-amber-900/30', border: 'border-amber-700', text: 'text-amber-300', badge: 'bg-amber-600 text-white' },
  soon: { bg: 'bg-blue-900/30', border: 'border-blue-700', text: 'text-blue-300', badge: 'bg-blue-600 text-white' },
};

const CATEGORY_ICONS = {
  deadline: Clock,
  hearing: AlertTriangle,
  alert: AlertTriangle,
  intake: FileText,
  draft: FileText,
  retention: Sparkles,
};

const MorningBriefing = () => {
  const [briefing, setBriefing] = useState(null);
  const [rawData, setRawData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [generating, setGenerating] = useState(false);
  const [briefingId, setBriefingId] = useState(null);
  const [sections, setSections] = useState({ overnight: false, cases: false, financial: false, astraea: false });
  const navigate = useNavigate();

  const fetchBriefing = async () => {
    try {
      setLoading(true);
      const resp = await api.get('/briefings/latest');
      setBriefing(resp.data.briefing || {});
      setRawData(resp.data.raw_data || {});
      setBriefingId(resp.data.id);

      // Mark as viewed
      if (!resp.data.viewed) {
        api.post(`/briefings/${resp.data.id}/viewed`).catch(() => {});
      }
    } catch (err) {
      if (err.response?.status !== 404) console.error(err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchBriefing(); }, []);

  const handleGenerate = async () => {
    try {
      setGenerating(true);
      const resp = await api.post('/briefings/generate');
      setBriefing(resp.data.briefing || {});
      setRawData(resp.data.raw_data || {});
      setBriefingId(resp.data.id);
    } catch (err) { console.error(err); }
    finally { setGenerating(false); }
  };

  const toggle = (key) => setSections(prev => ({ ...prev, [key]: !prev[key] }));

  if (loading) {
    return (
      <div className="bg-gradient-to-br from-slate-800 to-slate-900 rounded-xl p-6 border border-teal-700/30 mb-4">
        <div className="text-gray-500 animate-pulse">Loading briefing...</div>
      </div>
    );
  }

  if (!briefing || !briefing.headline) {
    return (
      <div className="bg-gradient-to-br from-slate-800 to-slate-900 rounded-xl p-6 border border-teal-700/30 mb-4">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Sun className="w-5 h-5 text-amber-400" />
            <span className="text-gray-400 text-sm">No briefing for today yet</span>
          </div>
          <button onClick={handleGenerate} disabled={generating}
            className="flex items-center gap-1 px-3 py-1.5 rounded text-xs font-medium bg-teal-700 hover:bg-teal-600 text-white">
            {generating ? <RefreshCw className="w-3.5 h-3.5 animate-spin" /> : <Sparkles className="w-3.5 h-3.5" />}
            Generate Now
          </button>
        </div>
      </div>
    );
  }

  const priorities = briefing.priority_items || [];
  const fyis = briefing.fyi_items || [];
  const drafts = rawData?.drafts || {};
  const astraea = rawData?.astraea_summary || {};
  const overnight = rawData?.overnight || {};
  const financial = rawData?.financial || {};
  const casesAttn = rawData?.cases_attention || {};

  const today = new Date().toLocaleDateString('en-US', { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' });

  return (
    <div className="bg-gradient-to-br from-slate-800 to-slate-900 rounded-xl border border-teal-700/30 mb-4 overflow-hidden">
      {/* Header */}
      <div className="px-6 py-4 border-b border-teal-800/30 flex items-center justify-between">
        <div>
          <div className="flex items-center gap-2">
            <Sun className="w-5 h-5 text-amber-400" />
            <h2 className="text-base font-semibold text-gray-200">Good morning, David</h2>
            <span className="text-[10px] px-2 py-0.5 rounded-full bg-teal-900/50 text-teal-300 border border-teal-700/50">Astraea</span>
          </div>
          <p className="text-xs text-gray-500 mt-0.5">{today}</p>
        </div>
        <button onClick={handleGenerate} disabled={generating}
          className="flex items-center gap-1 px-2 py-1 rounded text-xs text-gray-500 hover:text-teal-300 transition">
          <RefreshCw className={`w-3.5 h-3.5 ${generating ? 'animate-spin' : ''}`} />
          Refresh
        </button>
      </div>

      <div className="p-6 space-y-4">
        {/* Headline */}
        <p className="text-sm text-gray-300 leading-relaxed">{briefing.headline}</p>

        {/* Priority Items */}
        {priorities.length > 0 && (
          <div>
            <h3 className="text-xs font-semibold text-gray-500 uppercase mb-2">Priority Items</h3>
            <div className="space-y-1.5">
              {priorities.map((item, i) => {
                const s = URGENCY_STYLES[item.urgency] || URGENCY_STYLES.soon;
                const Icon = CATEGORY_ICONS[item.category] || FileText;
                return (
                  <div key={i} className={`flex items-start gap-2 rounded-lg p-2.5 border ${s.bg} ${s.border}`}>
                    <span className={`text-[10px] px-1.5 py-0.5 rounded font-bold ${s.badge} mt-0.5 whitespace-nowrap`}>
                      {item.urgency?.toUpperCase()}
                    </span>
                    <Icon className={`w-3.5 h-3.5 mt-0.5 ${s.text} flex-shrink-0`} />
                    <span className={`text-sm ${s.text} flex-1`}>
                      {item.link_id ? (
                        <span className="cursor-pointer hover:underline" onClick={() => {
                          if (item.link_type === 'case') navigate(`/cases/${item.link_id}`);
                          else if (item.link_type === 'alert') navigate(`/cases/${item.link_id}?tab=alerts`);
                        }}>
                          {item.text}
                        </span>
                      ) : item.text}
                    </span>
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {/* Drafts Ready */}
        {drafts.total > 0 && (
          <div className="bg-blue-900/20 border border-blue-800/30 rounded-lg p-3">
            <div className="flex items-center gap-2 text-sm text-blue-300">
              <FileText className="w-4 h-4" />
              <span className="font-medium">{drafts.total} drafts ready for review</span>
            </div>
            <div className="flex gap-4 mt-1 text-xs text-blue-400/70">
              {drafts.response_drafts > 0 && <span>{drafts.response_drafts} responses</span>}
              {drafts.client_update_drafts > 0 && <span>{drafts.client_update_drafts} client updates</span>}
              {drafts.hearing_preps_unreviewed > 0 && <span>{drafts.hearing_preps_unreviewed} hearing preps</span>}
              {drafts.engagement_letter_drafts > 0 && <span>{drafts.engagement_letter_drafts} engagement letters</span>}
              {drafts.reengagement_drafts > 0 && <span>{drafts.reengagement_drafts} re-engagement</span>}
            </div>
          </div>
        )}

        {/* Collapsible: Overnight */}
        <CollapsibleSection title="Overnight Activity" open={sections.overnight} onToggle={() => toggle('overnight')}>
          <div className="grid grid-cols-4 gap-3 text-center">
            <MiniStat label="eService" value={overnight.eservice?.count || 0} sub={overnight.eservice?.pending ? `${overnight.eservice.pending} pending` : null} />
            <MiniStat label="Intake Calls" value={overnight.intake?.count || 0} sub={overnight.intake?.missed ? `${overnight.intake.missed} missed` : null} />
            <MiniStat label="Portal Msgs" value={overnight.portal?.client_messages || 0} />
            <MiniStat label="Alerts" value={Object.values(overnight.alerts || {}).reduce((a, b) => a + b, 0)}
              sub={overnight.alerts?.critical ? `${overnight.alerts.critical} critical` : null} />
          </div>
          {briefing.overnight_summary && <p className="text-xs text-gray-400 mt-2">{briefing.overnight_summary}</p>}
        </CollapsibleSection>

        {/* Collapsible: Cases */}
        <CollapsibleSection title="Cases Needing Attention" open={sections.cases} onToggle={() => toggle('cases')}
          badge={((casesAttn.health_drops?.length || 0) + (casesAttn.stale_cases?.length || 0) + (casesAttn.unmatched_filings_48h || 0)) || null}>
          {casesAttn.health_drops?.length > 0 && (
            <div className="mb-2">
              <span className="text-[10px] text-gray-500">Health score drops:</span>
              {casesAttn.health_drops.map((d, i) => (
                <div key={i} className="text-xs text-red-400 ml-2 cursor-pointer hover:underline"
                  onClick={() => navigate(`/cases/${d.case_id}`)}>
                  Score {d.score} ({d.delta > 0 ? '+' : ''}{d.delta})
                </div>
              ))}
            </div>
          )}
          {casesAttn.stale_cases?.length > 0 && (
            <div className="mb-2">
              <span className="text-[10px] text-gray-500">{casesAttn.stale_cases.length} cases inactive 14+ days</span>
            </div>
          )}
          {casesAttn.unmatched_filings_48h > 0 && (
            <div className="text-xs text-amber-400">{casesAttn.unmatched_filings_48h} unmatched filings &gt;48h</div>
          )}
          {!casesAttn.health_drops?.length && !casesAttn.stale_cases?.length && !casesAttn.unmatched_filings_48h && (
            <div className="flex items-center gap-1 text-xs text-green-400"><CheckCircle className="w-3 h-3" /> All cases nominal</div>
          )}
        </CollapsibleSection>

        {/* Collapsible: Financial */}
        <CollapsibleSection title="Financial Snapshot" open={sections.financial} onToggle={() => toggle('financial')}>
          <div className="grid grid-cols-3 gap-3 text-center">
            <MiniStat label="Operating Cash" value={financial.operating_cash ? `$${(financial.operating_cash / 1000).toFixed(0)}k` : '—'} />
            <MiniStat label="AR Total" value={financial.total_ar ? `$${(financial.total_ar / 1000).toFixed(0)}k` : '—'}
              sub={financial.ar_90_plus ? `$${(financial.ar_90_plus / 1000).toFixed(0)}k 90+` : null} />
            <MiniStat label="Cash Runway" value={financial.cash_runway_days ? `${financial.cash_runway_days}d` : '—'} />
          </div>
          {financial.tax_due_soon && (
            <div className="text-xs text-amber-400 mt-2">
              Q{financial.tax_due_soon.quarter} estimated tax due {financial.tax_due_soon.due_date} ({financial.tax_due_soon.days_away} days)
            </div>
          )}
          {briefing.financial_note && <p className="text-xs text-gray-400 mt-2">{briefing.financial_note}</p>}
        </CollapsibleSection>

        {/* Collapsible: Astraea Summary */}
        <CollapsibleSection title="Astraea Overnight" open={sections.astraea} onToggle={() => toggle('astraea')}>
          <div className="flex flex-wrap gap-3 text-xs text-gray-400">
            {astraea.briefs_updated > 0 && <span>{astraea.briefs_updated} briefs updated</span>}
            {astraea.deadlines_created > 0 && <span>{astraea.deadlines_created} deadlines created</span>}
            {astraea.aliases_learned > 0 && <span>{astraea.aliases_learned} aliases learned</span>}
            {astraea.alerts_generated > 0 && <span>{astraea.alerts_generated} alerts generated</span>}
            {astraea.client_updates_generated > 0 && <span>{astraea.client_updates_generated} client updates drafted</span>}
            {astraea.retention_actions_surfaced > 0 && <span>{astraea.retention_actions_surfaced} retention actions</span>}
            {astraea.total_actions === 0 && <span>Quiet night — no autonomous actions taken</span>}
          </div>
        </CollapsibleSection>

        {/* FYI */}
        {fyis.length > 0 && (
          <div className="text-xs text-gray-500 space-y-0.5">
            {fyis.map((fyi, i) => <div key={i}>• {fyi}</div>)}
          </div>
        )}

        {/* Astraea sign-off */}
        {briefing.astraea_note && (
          <p className="text-xs text-teal-600 italic mt-2">{briefing.astraea_note}</p>
        )}
      </div>
    </div>
  );
};

const CollapsibleSection = ({ title, open, onToggle, badge, children }) => (
  <div className="border border-gray-700/50 rounded-lg overflow-hidden">
    <div className="flex items-center justify-between px-3 py-2 cursor-pointer hover:bg-gray-800/30 transition" onClick={onToggle}>
      <div className="flex items-center gap-2">
        <span className="text-xs font-semibold text-gray-400 uppercase">{title}</span>
        {badge && <span className="text-[10px] px-1.5 py-0.5 rounded-full bg-amber-900/50 text-amber-300">{badge}</span>}
      </div>
      <ChevronDown className={`w-3.5 h-3.5 text-gray-600 transition-transform ${open ? 'rotate-180' : ''}`} />
    </div>
    {open && <div className="px-3 pb-3 pt-1">{children}</div>}
  </div>
);

const MiniStat = ({ label, value, sub }) => (
  <div className="bg-gray-800/40 rounded p-2">
    <div className="text-sm font-bold text-gray-300">{value}</div>
    <div className="text-[10px] text-gray-500">{label}</div>
    {sub && <div className="text-[10px] text-amber-400">{sub}</div>}
  </div>
);

export default MorningBriefing;
